import { createContext, type ReactNode, use } from 'react';
import type { FindingsFilter } from 'ts/commons/filters/findings/FindingsFilter';
import { EFindingsFilterTab } from 'ts/commons/filters/findings/FindingsSummaryFilter';
import type { FindingsSummaryInfo } from 'typedefs/FindingsSummaryInfo';
import {
	type ExposedFindingCategoryFilterContext,
	type FindingCategoryFilter,
	FindingCategoryFilterContextProvider,
	getCategoryFilterContext
} from './FindingCategoryFilterContext';

type FindingFilterContextProviderProps = {
	findingsSummary: FindingsSummaryInfo;
	findingsFilter: FindingsFilter;
	onFindingsFilterChanged: (findingsFilter: FindingsFilter, affectedTab: EFindingsFilterTab) => void;
	children: ReactNode;
};

/** Exposes functions to manipulate the findings filter options of the current view. */
export function FindingFilterContextProvider({
	findingsSummary,
	findingsFilter,
	onFindingsFilterChanged,
	children
}: FindingFilterContextProviderProps) {
	const onCategoryChange = (categoryFilter: FindingCategoryFilter) => {
		onFindingsFilterChanged(findingsFilter.cloneWith(categoryFilter), EFindingsFilterTab.CATEGORIES);
	};
	const findingCategoryContext = getCategoryFilterContext(
		findingsSummary,
		findingsFilter.getCategoryGroupTypeFilter(),
		onCategoryChange
	);
	const addPath = (selectedPath: string, included: boolean) => {
		if (included) {
			findingsFilter.addIncludedPath(selectedPath);
		} else {
			findingsFilter.addExcludedPath(selectedPath);
		}
		onFindingsFilterChanged(findingsFilter, EFindingsFilterTab.TEXT);
	};
	return (
		<FindingFilterContext value={{ ...findingCategoryContext, addPath }}>
			<FindingCategoryFilterContextProvider
				findingSummary={findingsSummary}
				findingsCategoryFilter={findingsFilter.getCategoryGroupTypeFilter()}
				onChange={onCategoryChange}
			>
				{children}
			</FindingCategoryFilterContextProvider>
		</FindingFilterContext>
	);
}

/** Exposed filter options. */
export type ExposedFindingFilterContext = ExposedFindingCategoryFilterContext & {
	addPath: (path: string, included: boolean) => void;
};

/** Context that holds the current state of the findings filter. */
const FindingFilterContext = createContext<ExposedFindingFilterContext | undefined>(undefined);

/** Returns the FindingFilterContext or undefined if the component is not wrapped in a FindingFilterContextProvider. */
export function useFindingFilterContextIfAvailable(): ExposedFindingFilterContext | undefined {
	return use(FindingFilterContext);
}
